import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Pause, Play, X } from 'lucide-react';
import { IconButton } from '../../../components/ui';
import { formatMonth } from './networkModel';

interface TimeScrubberProps {
  extent: { min: number; max: number };
  value: number;
  onChange: (t: number) => void;
  playing: boolean;
  onPlayingChange: (playing: boolean) => void;
  histogram: { starts: number[]; ends: number[] };
  /** active ties at the current time */
  activeTies: number;
  onClose: () => void;
  reducedMotion: boolean;
  className?: string;
}

const PLAY_MS = 14000;

/** Bottom time bar: sparkline of ties starting (above) and ending (below), play/pause, date */
export const TimeScrubber: React.FC<TimeScrubberProps> = ({
  extent,
  value,
  onChange,
  playing,
  onPlayingChange,
  histogram,
  activeTies,
  onClose,
  reducedMotion,
  className = '',
}) => {
  const span = extent.max - extent.min || 1;
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!playing) return;
    let frame = 0;
    let last = performance.now();
    // Restart from the beginning when playing at the end (the ref is updated right away,
    // the next render is not guaranteed before the first frame)
    const advance = (t: number) => {
      valueRef.current = t;
      onChangeRef.current(t);
    };
    if (valueRef.current >= extent.max - span * 0.005) advance(extent.min);
    const step = (now: number) => {
      const dt = Math.max(0, now - last);
      last = now;
      const next = valueRef.current + (span * dt) / PLAY_MS;
      if (next >= extent.max) {
        advance(extent.max);
        onPlayingChange(false);
        return;
      }
      advance(next);
      frame = requestAnimationFrame(step);
    };
    if (reducedMotion) {
      // Yearly steps instead of a continuous sweep
      const timer = window.setInterval(() => {
        const next = valueRef.current + 365 * 86400000;
        if (next >= extent.max) {
          advance(extent.max);
          onPlayingChange(false);
        } else advance(next);
      }, 700);
      return () => window.clearInterval(timer);
    }
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [playing, extent, span, onPlayingChange, reducedMotion]);

  const peak = useMemo(() => Math.max(1, ...histogram.starts, ...histogram.ends), [histogram]);
  // As many year labels as the track has room for (about 36px each), so they never overlap on a phone
  const trackRef = useRef<HTMLDivElement>(null);
  const [trackWidth, setTrackWidth] = useState(0);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    setTrackWidth(track.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setTrackWidth(track.clientWidth));
    observer.observe(track);
    return () => observer.disconnect();
  }, []);
  const maxLabels = trackWidth ? Math.max(2, Math.min(6, Math.floor(trackWidth / 36))) : 6;

  const years = useMemo(() => {
    const out: Array<{ t: number; label: string }> = [];
    const y0 = new Date(extent.min).getUTCFullYear();
    const y1 = new Date(extent.max).getUTCFullYear();
    const stepYears = Math.max(1, Math.ceil((y1 - y0) / maxLabels));
    for (let y = Math.ceil(y0 / stepYears) * stepYears; y <= y1; y += stepYears) {
      const t = Date.UTC(y, 0, 1);
      if (t >= extent.min && t <= extent.max) out.push({ t, label: String(y) });
    }
    return out;
  }, [extent, maxLabels]);
  const pct = ((value - extent.min) / span) * 100;
  const buckets = histogram.starts.length;

  return (
    <div
      className={`flex w-full max-w-[620px] items-center gap-2 rounded-2xl bg-white py-1.5 pl-1.5 pr-2 shadow-[0_8px_28px_-12px_rgba(0,0,0,0.22)] ring-1 ring-black/[0.06] ${className}`}
      role="group"
      aria-label="Time"
    >
      <IconButton label={playing ? 'Pause' : 'Play'} size="sm" onClick={() => onPlayingChange(!playing)} className="bg-neutral-100 !text-ink hover:bg-neutral-200">
        {playing ? <Pause className="h-4 w-4 fill-current" strokeWidth={1.5} /> : <Play className="ml-0.5 h-4 w-4 fill-current" strokeWidth={1.5} />}
      </IconButton>
      <div className="w-[76px] flex-shrink-0 leading-tight">
        <p className="text-[13px] tabular-nums text-ink">{formatMonth(value)}</p>
        <p className="text-[11px] tabular-nums text-neutral-400">
          {activeTies} {activeTies === 1 ? 'tie' : 'ties'}
        </p>
      </div>
      <div ref={trackRef} className="relative min-w-0 flex-1 pb-3 pt-1">
        <svg className="block h-7 w-full" viewBox={`0 0 ${buckets} 28`} preserveAspectRatio="none" aria-hidden="true">
          {histogram.starts.map((n, i) => (
            <rect key={`s${i}`} x={i + 0.15} width={0.7} y={14 - (n / peak) * 12} height={(n / peak) * 12} fill={(i + 0.5) / buckets * 100 <= pct ? '#0a0a0a' : '#d4d4d4'} />
          ))}
          {histogram.ends.map((n, i) => (
            <rect key={`e${i}`} x={i + 0.15} width={0.7} y={15} height={(n / peak) * 8} fill={(i + 0.5) / buckets * 100 <= pct ? '#a3a3a3' : '#e5e5e5'} />
          ))}
        </svg>
        <input
          type="range"
          min={extent.min}
          max={extent.max}
          step={86400000}
          value={value}
          onChange={(e) => {
            if (playing) onPlayingChange(false);
            onChange(Number(e.target.value));
          }}
          aria-label="Date"
          aria-valuetext={formatMonth(value)}
          className="absolute inset-x-0 top-0 h-8 w-full cursor-pointer appearance-none bg-transparent opacity-0"
        />
        <span className="pointer-events-none absolute top-0 h-8 w-px bg-primary-600" style={{ left: `${pct}%` }} aria-hidden="true">
          <span className="absolute -left-[3px] -top-[3px] h-[7px] w-[7px] rounded-full bg-primary-600" />
        </span>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-3">
          {years.map((y) => (
            <span
              key={y.t}
              className="absolute -translate-x-1/2 text-[10px] tabular-nums text-neutral-400"
              style={{ left: `${((y.t - extent.min) / span) * 100}%` }}
            >
              {y.label}
            </span>
          ))}
        </div>
      </div>
      <IconButton label="Close time view" size="xs" onClick={onClose}>
        <X className="h-3.5 w-3.5" strokeWidth={1.75} />
      </IconButton>
    </div>
  );
};
